import React from 'react';
import PropTypes from 'prop-types';
import {FormattedMessage, injectIntl} from "react-intl";

import {useFormikContext} from "formik";

import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";

import IconButton from "components/button/IconButton";
import TrashIcon from "components/icons/TrashIcon";
import InputTextField from "components/input/new/InputTextField";

import {MetadataTypes, getMetadataTypes} from "../../utils/metadata";

MetadataRow.propTypes = {
    index: PropTypes.number.isRequired,
    metadata: PropTypes.shape({
        key: PropTypes.string,
        type: PropTypes.string,
        value: PropTypes.string,
    }).isRequired,
    onDelete: PropTypes.func.isRequired,
};

function MetadataRow({index, metadata, onDelete, ...props}) {
    const {setFieldValue, setFieldTouched, handleBlur, touched, errors} = useFormikContext();

    const rowErrors = errors?.metadata?.[index] ?? {};
    const rowTouched = touched?.metadata?.[index] ?? {};

    function getFieldName(field) {
        return `metadata[${index}].${field}`;
    }

    function onTypeChange(type) {
        setFieldValue(getFieldName('type'), type);

        // the previous value is most likely not castable to the new type
        if (type === MetadataTypes.BOOLEAN) {
            setFieldValue(getFieldName('value'), metadata.value === 'true' ? 'true' : 'false');
        } else if (type === MetadataTypes.NUMBER && isNaN(Number(metadata.value))) {
            setFieldValue(getFieldName('value'), '');
        }

        setFieldTouched(getFieldName('value'), true);
    }

    return (
        <Grid item container columnSpacing={1} alignItems={'flex-start'} flexWrap={'nowrap'}>
            <Grid item xs={4}>
                <InputTextField
                    name={getFieldName('key')}
                    value={metadata.key}
                    onChange={(event) => setFieldValue(getFieldName('key'), event.target.value)}
                    onBlur={handleBlur}
                    placeholder={props.intl.formatMessage({id: 'shippingMethods.form.field.metadata.key.placeholder'})}
                    fullWidth
                    error={Boolean(rowTouched.key) && Boolean(rowErrors.key)}
                    helperText={Boolean(rowTouched.key) && rowErrors.key}
                />
            </Grid>
            <Grid item xs={3}>
                <InputTextField
                    select
                    name={getFieldName('type')}
                    value={metadata.type}
                    onChange={(event) => onTypeChange(event.target.value)}
                    fullWidth
                >
                    {getMetadataTypes().map((type) => (
                        <MenuItem key={type} value={type}>
                            <FormattedMessage id={`shippingMethods.form.field.metadata.type.${type}`} />
                        </MenuItem>
                    ))}
                </InputTextField>
            </Grid>
            <Grid item xs={4}>
                {metadata.type === MetadataTypes.BOOLEAN ? (
                    <InputTextField
                        select
                        name={getFieldName('value')}
                        value={metadata.value === 'true' ? 'true' : 'false'}
                        onChange={(event) => setFieldValue(getFieldName('value'), event.target.value)}
                        fullWidth
                    >
                        <MenuItem value={'true'}>
                            <FormattedMessage id={'shippingMethods.form.field.metadata.value.true'} />
                        </MenuItem>
                        <MenuItem value={'false'}>
                            <FormattedMessage id={'shippingMethods.form.field.metadata.value.false'} />
                        </MenuItem>
                    </InputTextField>
                ) : (
                    <InputTextField
                        name={getFieldName('value')}
                        value={metadata.value}
                        onChange={(event) => setFieldValue(getFieldName('value'), event.target.value)}
                        onBlur={handleBlur}
                        placeholder={props.intl.formatMessage({id: `shippingMethods.form.field.metadata.value.${metadata.type}.placeholder`})}
                        fullWidth
                        error={Boolean(rowTouched.value) && Boolean(rowErrors.value)}
                        helperText={Boolean(rowTouched.value) && rowErrors.value}
                    />
                )}
            </Grid>
            <Grid item xs={1} sx={{paddingTop: '8px'}}>
                <IconButton onClick={() => onDelete(index)}>
                    <TrashIcon />
                </IconButton>
            </Grid>
        </Grid>
    );
}

export default injectIntl(MetadataRow);
